const React = require('react');
const ReactDOMClient = require('react-dom/client');
const Shared = require("./shared.js");
const Components = require('../codegen/global_exports');
const prerenderFields = require('./prerender-fields.json');

const initPageHeaderScripts = window.Swp?.React?.PageHeaderInit ?? window.Swp?.React?.Init ?? [];

/** 
 * Retrieves the stack of properties that have the `prerenderSerialization` flag set to `true`
 * in a given component object.
 *
 * @param {object} component - The component object to traverse.
 * @param {string} [path=""] - The current path in the component object.
 * @param {string[]} [stack=[]] - The stack of properties with `prerenderSerialization` flag set to `true`.
 * @returns {string[]} - The stack of properties with `prerenderSerialization` flag set to `true`.
 */
function getPrerenderStack(component, path = "", stack = []) {
  for (const prop in component) {
    if (typeof component[prop] == "object" && component[prop] !== null) {
      getPrerenderStack(component[prop], path === "" ? prop : `${path}.${prop}`, stack);
      if (component[prop].prerenderSerialization === true) {
        // add for bottom up prerendering
        stack.push(path === "" ? prop : `${path}.${prop}`);
      }
    }
  }
  return stack;
}

function getPropsFromParent(parent, keys) {
  return keys.reduce((obj, key) => {
    if (parent[key]) {
      obj[key] = parent[key];
    }
    return obj;
  }, {});
}

function getPropsToRender(parent, componentKey) {
  const existingProps = parent[componentKey];
  if (!existingProps.extractParentProps) return existingProps;
  const parentProps = getPropsFromParent(parent, existingProps.extractParentProps);
  return {
    templateName: existingProps.templateName,
    ...parentProps
  }
}

/**
 * Renders a chain of components based on the given paths and component data.
 *
 * @param {string[]} paths - The paths of the components to render.
 * @param {Function} renderComponent - The function to render the final component.
 * @param {Object} componentData - The data object containing the components.
 * @returns {void}
 */
function chainRenders(paths = [], renderComponent = ()=>{}, componentData = {}) {
  if (paths.length === 0) {
    renderComponent();
    return;
  }

  const path = paths.pop();
  const parent = path.split(".").slice(0, -1).reduce((obj, key) => obj[key], componentData);
  const componentKey = path.split(".").pop();
  const props = getPropsToRender(parent, componentKey);
  const fn = Components[props.templateName ?? '!'];
  fn((element) => {
    const reactObject = React.createElement(element, props);
    parent[componentKey] = reactObject;
    chainRenders(paths, renderComponent, componentData);
  });
}


window.addEventListener('load', () => {
  initPageHeaderScripts.forEach((initScript, i) => {
    if (prerenderFields.hasOwnProperty(initScript.name)) {
      Object.entries(prerenderFields[initScript.name]).forEach(([field, value]) => {
        if (initScript.props.hasOwnProperty(field)) {
          initPageHeaderScripts[i].props[field].prerenderSerialization = true;
          initPageHeaderScripts[i].props[field].templateName = value
        } else if (field.includes("!")) {
          const propKey = field.replace("!", "");
          initPageHeaderScripts[i].props[propKey] ??= {};
          initPageHeaderScripts[i].props[propKey].prerenderSerialization = true;
          initPageHeaderScripts[i].props[propKey].templateName = value
          initPageHeaderScripts[i].props[propKey].extractParentProps = prerenderFields[field];
        }
      });
    }

    const target = window.document.getElementById(`id-${initScript.props.id}`);
    const componentImportFn = Components[initScript.name];
    Shared.parseTree(initScript.props);
    const renderComponent = () => {
      if (componentImportFn) {
        componentImportFn((element) => {
          const reactObject = React.createElement(element, initScript.props);
          if(initScript.props.reactInit === "Client"){
            ReactDOMClient.createRoot(target).render(reactObject);
          }
          else {
            ReactDOMClient.hydrateRoot(target, reactObject);
          }
        });
      }
    }
    const prerenderStack = getPrerenderStack(initScript.props);
    chainRenders(prerenderStack, renderComponent, initScript.props);
  });

  const initPageBodyScripts = window.Swp?.React?.PageBodyInit ?? [];

  initPageBodyScripts.forEach((initScript, i) => {
    if (prerenderFields.hasOwnProperty(initScript.name)) {
      Object.entries(prerenderFields[initScript.name]).forEach(([field, value]) => {
        if (initScript.props.hasOwnProperty(field)) {
          if (Array.isArray(initPageBodyScripts[i].props[field])) {
            initPageBodyScripts[i].props[field].forEach((item, j) => {
              item.prerenderSerialization = true;
              item.templateName = value
              item.key = j;
            });
          } else {
            initPageBodyScripts[i].props[field].prerenderSerialization = true;
            initPageBodyScripts[i].props[field].templateName = value
          }
        } else if (field.includes("!")) {
          const propKey = field.replace("!", "");
          initPageBodyScripts[i].props[propKey] ??= {};
          initPageBodyScripts[i].props[propKey].prerenderSerialization = true;
          initPageBodyScripts[i].props[propKey].templateName = value
          initPageBodyScripts[i].props[propKey].extractParentProps = prerenderFields[field];
        }
      });
    }
  });

  async function initPageBodyComponent(target) {
    if (target) {
      const initScript = initPageBodyScripts.find((item) => { return `id-${item.props.id}` === target.id; } );
      if (initScript) {
        const componentImportFn = Components[initScript.name];
        Shared.parseTree(initScript.props);
        const renderComponent = () => {
          if (componentImportFn) {
            componentImportFn((element) => {
              const reactObject = React.createElement(element, initScript.props);
              if(initScript.props.reactInit === "Client"){
                ReactDOMClient.createRoot(target).render(reactObject);
              }
              else {
                ReactDOMClient.hydrateRoot(target, reactObject);
              }
            });
          }
        }
        const prerenderStack = getPrerenderStack(initScript.props);
        chainRenders(prerenderStack, renderComponent, initScript.props);
      }
    }
  }

  const observerOptions = {
    root: null,
    threshold: 0,
    rootMargin: "0px 0px 300px 0px"
  };

  const pageBodyIO = new IntersectionObserver((entries) => {
    entries.forEach(({isIntersecting, target}) => {
      if (isIntersecting) {
        pageBodyIO.unobserve(target);
        initPageBodyComponent(target);
      }
    })
  }, observerOptions);

  if (initPageBodyScripts.length > 0) {
    initPageBodyScripts.forEach(initScript => {
      const element = window.document.getElementById(`id-${initScript.props.id}`);

      if (element) {
        pageBodyIO.observe(element);
      }
    });
  }
});